<template>
  <v-alert
    v-if="visible"
    type="warning"
    variant="flat"
    density="comfortable"
    class="email-verification-banner"
  >
    <div class="email-verification-banner__content">
      <div v-if="pendingEmail" class="email-verification-banner__text">
        Tienes un cambio de correo pendiente a <strong>{{ pendingEmail }}</strong>. Ábrelo desde
        esa bandeja y confírmalo para completar el cambio.
      </div>
      <div v-else class="email-verification-banner__text">
        Para mayor seguridad, confirma tu correo <strong>{{ email }}</strong>. Así podremos
        ayudarte a recuperar tu cuenta si pierdes el acceso.
      </div>

      <div class="email-verification-banner__actions">
        <v-btn
          :disabled="remainingSeconds > 0 || isSending"
          :loading="isSending"
          color="primary"
          variant="flat"
          size="small"
          @click="resend"
        >
          {{ resendText }}
        </v-btn>

        <v-btn
          v-if="pendingEmail"
          :loading="isCanceling"
          variant="text"
          size="small"
          @click="cancelChange"
        >
          Cancelar cambio
        </v-btn>
        <v-btn v-else :loading="isSyncing" variant="text" size="small" @click="syncStatus()">
          Ya confirmé
        </v-btn>
      </div>
    </div>
  </v-alert>
</template>

<script lang="ts">
import { computed, defineComponent, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'

import { useAuthStore } from '../../stores/auth'
import { useUserProfileStore } from '../../stores/userProfile'
import { useEmailVerification } from '../../composables/useEmailVerification'
import { useToast } from '../../composables/useToast'

const HIDDEN_ROUTE_NAMES = ['confirm-email', 'email-verification']

export default defineComponent({
  name: 'EmailVerificationBanner',

  setup() {
    const authStore = useAuthStore()
    const userProfileStore = useUserProfileStore()
    const route = useRoute()
    const { triggerToast } = useToast()
    const { remainingSeconds, isSending, resend } = useEmailVerification()

    const isSyncing = ref(false)
    const isCanceling = ref(false)

    const email = computed(
      () => userProfileStore.profile?.email || (authStore.currentUser as any)?.email || '',
    )
    const pendingEmail = computed(() => userProfileStore.profile?.pendingEmail || '')

    const visible = computed(() => {
      if (authStore.userType !== 'registered') return false
      if (HIDDEN_ROUTE_NAMES.includes(String(route.name))) return false
      return !!pendingEmail.value || !authStore.emailVerified
    })

    const resendText = computed(() =>
      remainingSeconds.value > 0 ? `Reenviar (${remainingSeconds.value})` : 'Reenviar correo',
    )

    /**
     * Pulls the confirmation state from the server, so a confirmation done on another
     * device stops showing this banner here.
     */
    const syncStatus = async (options: { silent?: boolean } = {}) => {
      const { silent = false } = options
      if (isSyncing.value) return

      isSyncing.value = true
      try {
        await authStore.refresh()
        const userId = authStore.currentUser?.userId
        if (userId) {
          await userProfileStore.fetchProfile(userId)
        }
        if (!silent && !authStore.emailVerified && !pendingEmail.value) {
          triggerToast({
            message: 'Todavía no recibimos la confirmación de tu correo',
            color: 'warning',
          })
        }
      } catch (error) {
        console.error('Error syncing email verification status:', error)
      } finally {
        isSyncing.value = false
      }
    }

    const cancelChange = async () => {
      isCanceling.value = true
      try {
        await authStore.cancelEmailChange()
        const userId = authStore.currentUser?.userId
        if (userId) {
          await userProfileStore.fetchProfile(userId)
        }
        triggerToast({
          message: 'Cancelamos el cambio de correo',
          color: 'success',
        })
      } catch (error) {
        console.error('Error canceling email change:', error)
      } finally {
        isCanceling.value = false
      }
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && visible.value) {
        syncStatus({ silent: true })
      }
    }

    onMounted(() => {
      document.addEventListener('visibilitychange', onVisibilityChange)
    })

    onBeforeUnmount(() => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
    })

    return {
      visible,
      email,
      pendingEmail,
      remainingSeconds,
      isSending,
      isSyncing,
      isCanceling,
      resendText,
      resend,
      syncStatus,
      cancelChange,
    }
  },
})
</script>

<style scoped>
.email-verification-banner {
  position: relative;
  z-index: 1;
  border-radius: 0;
  min-height: 96px;
  padding: 20px 24px;
  background: rgb(var(--v-theme-warning)) !important;
  color: rgb(var(--v-theme-on-warning)) !important;
}

.email-verification-banner__content {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 20px;
  flex-wrap: wrap;
}

.email-verification-banner__text {
  max-width: 720px;
  font-size: 1rem;
  line-height: 1.5;
}

.email-verification-banner__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
</style>
